import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import getAPIbaseURL from "../../utils/helpers";
import i18n from "../../i18n";
import { getBrowserTimeZone } from "../../utils/timeLogging";

const API_URL = `${getAPIbaseURL()}/time-balances`;

export const fetchTimeBalances = createAsyncThunk(
  "timeBalances/fetchTimeBalances",
  async ({ userId, year, month, range }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, '');
      const params = { year, tz: getBrowserTimeZone() };
      if (month) params.month = month;
      if (range) params.range = range;

      const response = await axios.get(`${API_URL}/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.timeBalancesSlice.fetchTimeBalancesError"));
    }
  }
);

export const fetchBalanceSummary = createAsyncThunk(
  "timeBalances/fetchBalanceSummary",
  async (userId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, '');
      const response = await axios.get(
        `${API_URL}/user/${userId}/balance-summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.timeBalancesSlice.fetchBalanceSummaryError"));
    }
  }
);

export const updateDailyProjectHours = createAsyncThunk(
  "timeBalances/updateDailyProjectHours",
  async ({ userId, date, clockData, pauses, projectAllocations }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, '');
      const response = await axios.post(
        `${API_URL}/user/${userId}/allocate`,
        { userId, date, clockData, pauses, projectAllocations, timezone: getBrowserTimeZone() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const [year, month] = date.split('-');
      thunkAPI.dispatch(fetchTimeBalances({ userId, year: Number(year), month: Number(month) }));
      thunkAPI.dispatch(fetchBalanceSummary(userId)); 

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.timeBalancesSlice.updateProjectHoursError"));
    }
  }
);

export const fetchActivityTypes = createAsyncThunk(
  "timeBalances/fetchActivityTypes",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
      const response = await axios.get(`${getAPIbaseURL()}/time-balances/activity-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.timeBalancesSlice.fetchActivityTypesError"));
    }
  }
);

const timeBalancesSlice = createSlice({
  name: "timeBalances",
  initialState: {
    items: [],
    balanceSummary: {
      accumulated_approved_minutes: 0,
      current_month_minutes: 0,
    },
    activityTypes: [],
    isLoading: false,
    isActivityLoading: false,
    isSummaryLoading: false,
    error: null,
    message: null,
  },
  reducers: {
    clearTimeBalanceState: (state) => {
      state.message = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTimeBalances.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTimeBalances.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload; 
      })
      .addCase(fetchTimeBalances.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchBalanceSummary.pending, (state) => {
        state.isSummaryLoading = true;
      })
      .addCase(fetchBalanceSummary.fulfilled, (state, action) => {
        state.isSummaryLoading = false;
        state.balanceSummary = action.payload;
      })
      .addCase(fetchBalanceSummary.rejected, (state, action) => {
        state.isSummaryLoading = false;
        state.error = action.payload;
      })
      .addCase(updateDailyProjectHours.fulfilled, (state) => {
        state.message = i18n.t("slices.timeBalancesSlice.updateProjectHoursSuccess");
      })
      .addCase(fetchActivityTypes.pending, (state) => {
        state.isActivityLoading = true;
        state.error = null;
      })
      .addCase(fetchActivityTypes.fulfilled, (state, action) => {
        state.isActivityLoading = false; 
        state.activityTypes = action.payload;
      })
      .addCase(fetchActivityTypes.rejected, (state, action) => {
        state.isActivityLoading = false;
        state.error = action.payload;
      })
      .addMatcher(
        (action) => action.type.startsWith("timeBalances/") && action.type.endsWith("/rejected"),
        (state, action) => {
          state.error = action.payload;
          state.isLoading = false;
        }
      );
  },
});

export const { clearTimeBalanceState } = timeBalancesSlice.actions;
export default timeBalancesSlice.reducer;